<template>
    <div class="ppv2-w-full">
        <form @submit.prevent="save">
            <!-- Visual Identity -->
            <div class="md:ppv2-w-[80%] ppv2-w-full ppv2-p-6 ppv2-mb-8">
                <h2 class="poppinsSemiBold ppv2-mb-6" :style="{ fontSize: normalizeFontSize(20) }">
                    {{ $t('website_settings.general.visual_identity') }}
                </h2>
                <div class="ppv2-grid ppv2-grid-cols-1 lg:ppv2-grid-cols-2 ppv2-gap-8">
                    <!-- Logo Upload -->
                    <div class="lg:ppv2-col-span-1">
                        <CustomInputUpload
                            :label="$t('website_settings.general.logo')"
                            customClassLabel="poppinsLight ppv2-mb-2"
                            v-model="form.logo_url"
                            @input="(file) => handleImageUpload('logo', { target: { files: [file] } })"
                            specificTaille="130×40px"
                            classContainerAdd="!ppv2-mb-0"
                        />
                    </div>

                    <!-- Colors Grid -->
                    <div class="lg:ppv2-col-span-1">
                        <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-6">
                            <BaseColorInput
                                ref="field_primary_color"
                                v-model="form.primary_color"
                                :label="$t('website_settings.general.primary_color')"
                                customClassLabel="poppinsLight ppv2-mb-2"
                                :required="true"
                                :disabled="false"
                                :errorText="errors.primary_color"
                            />
                            <BaseColorInput
                                ref="field_secondary_color"
                                v-model="form.secondary_color"
                                :label="$t('website_settings.general.secondary_color')"
                                customClassLabel="poppinsLight ppv2-mb-2"
                                :required="true"
                                :disabled="false"
                                :errorText="errors.secondary_color"
                            />
                            <BaseColorInput
                                ref="field_menus_color"
                                v-model="form.menus_color"
                                :label="$t('website_settings.general.tertiary_color')"
                                customClassLabel="poppinsLight ppv2-mb-2"
                                :required="true"
                                :disabled="false"
                                :errorText="errors.menus_color"
                            />
                            <BaseColorInput
                                ref="field_action_text_color"
                                v-model="form.action_text_color"
                                :label="$t('website_settings.general.action_text_color')"
                                customClassLabel="poppinsLight ppv2-mb-2"
                                :required="true"
                                :disabled="false"
                                :errorText="errors.action_text_color"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Theme Selection -->
            <div class="ppv2-p-6 ppv2-mb-8">
                <h2 class="poppinsSemiBold ppv2-mb-6" :style="{ fontSize: normalizeFontSize(20) }">
                    {{ $t('website_settings.general.theme') }}
                </h2>
                <div
                    class="ppv2-grid ppv2-grid-cols-1 sm:ppv2-grid-cols-2 lg:ppv2-grid-cols-4"
                    :style="{ gap: normalizeSize(16, true) }"
                >
                    <div v-for="template in templates" :key="template.id">
                        <div
                            class="template-card ppv2-relative ppv2-cursor-pointer ppv2-transition-all ppv2-duration-300"
                            :class="{ 'selected bg-purple ppv2-bg-opacity-[15%]': selectedTemplate === template.id }"
                            @click="selectTemplate(template.id)"
                        >
                            <div class="ppv2-p-4 ppv2-pb-0">
                                <div class="ppv2-flex ppv2-justify-between ppv2-items-start ppv2-mb-2">
                                    <div>
                                        <h4 class="poppinsSemiBold ppv2-m-0" :style="{ fontSize: normalizeFontSize(15) }">
                                            {{ $t('website_settings.general.themes.v' + template.id + '.title') }}
                                        </h4>
                                        <p class="ppv2-text-gray-500 ppv2-m-0" :style="{ fontSize: normalizeFontSize(14) }">
                                            {{ $t('website_settings.general.themes.v' + template.id + '.desc') }}
                                        </p>
                                    </div>
                                    <div
                                        class="ppv2-w-[26px] ppv2-min-w-5 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-justify-center bg-purple ppv2-bg-opacity-[15%]"
                                    >
                                        <Check v-if="selectedTemplate === template.id" :size="12" class="text-purple" />
                                    </div>
                                </div>
                                <img
                                    :src="'/media/siteInternet/theme' + template.id + '.png'"
                                    class="ppv2-w-full ppv2-shadow-sm"
                                    :alt="template.name"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Home Banner -->
            <div
                ref="field_bannerMedia"
                class="section-block ppv2-w-full md:ppv2-w-[80%] ppv2-p-0 ppv2-mb-8 ppv2-overflow-hidden"
            >
                <div class="ppv2-p-6 ppv2-border-b ppv2-border-gray-100">
                    <h2 class="poppinsSemiBold ppv2-m-0" :style="{ fontSize: normalizeFontSize(20) }">
                        {{ $t('website_settings.general.home_banner') }}
                    </h2>
                </div>

                <div class="ppv2-p-6">
                    <div class="ppv2-grid ppv2-grid-cols-1 ppv2-gap-6 ppv2-mb-8">
                        <div>
                            <CustomInput
                                ref="field_title_welcome"
                                v-model="form.title_welcome"
                                :label="$t('website_settings.general.welcome_title')"
                                required
                                :placeholder="$t('website_settings.general.welcome_title_placeholder')"
                                :errorText="errors.title_welcome"
                            />
                        </div>
                        <div>
                            <BaseTextarea
                                ref="field_desc_welcome"
                                :label="$t('website_settings.general.welcome_subtitle')"
                                required
                                v-model="form.desc_welcome"
                                :rows="3"
                                :useEditor="false"
                                :placeholder="$t('website_settings.general.welcome_subtitle_placeholder')"
                                classContainerAdd="!ppv2-mb-0"
                                :error="errors.desc_welcome"
                                :maxLength="160"
                            />
                            <div class="ppv2-flex ppv2-w-full ppv2-justify-end poppinsLight ppv2-text-12 ppv2-text-gray-400">
                                {{ form.desc_welcome ? form.desc_welcome.length : 0 }}/160
                            </div>
                        </div>
                    </div>

                    <!-- Dynamic Section based on Theme -->
                    <div v-if="selectedTemplate === 1">
                        <div v-for="(img, idx) in sliderImages" :key="img.id || idx" class="ppv2-relative ppv2-w-full ppv2-mb-6">
                            <CustomInputUpload
                                :label="idx === 0 ? $t('website_settings.general.image_right') : ''"
                                v-model="sliderImages[idx].url"
                                :required="idx === 0"
                                @input="(file) => handleSliderUpload(idx, file)"
                                :errorText="errors.sliderImages"
                                :enableCropper="false"
                            />
                            <button
                                v-if="sliderImages.length > 1"
                                type="button"
                                @click="sliderImages.splice(idx, 1)"
                                class="ppv2-absolute ppv2-top-2 ppv2-right-2 ppv2-bg-white ppv2-rounded-full ppv2-p-1 ppv2-shadow-md hover:ppv2-bg-red-50 ppv2-transition-colors ppv2-z-[60]"
                            >
                                <X :size="14" class="ppv2-text-red-500" />
                            </button>
                        </div>
                        <div v-if="sliderImages.length < 5" class="ppv2-flex ppv2-justify-center">
                            <BaseButton variant="secondary" :customFontSize="12" @click="addSliderImage">
                                <Plus :size="12" /> {{ $t('website_settings.general.add_image') }}
                            </BaseButton>
                        </div>
                    </div>

                    <div v-else-if="selectedTemplate === 2">
                        <div class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-4">
                            <BaseLabel :label="$t('website_settings.general.carousel')" required />
                        </div>
                        <div
                            class="ppv2-flex ppv2-flex-col ppv2-justify-center ppv2-items-center"
                            :style="{ gap: normalizeSize(16, true) }"
                        >
                            <div v-for="(img, idx) in sliderImages" :key="img.id || idx" class="ppv2-relative ppv2-w-full">
                                <SliderImageUpload
                                    v-model="sliderImages[idx]"
                                    recommended-size="1920×1080px"
                                    :displayRemoveButton="false"
                                />
                                <button
                                    v-if="sliderImages.length > 1"
                                    type="button"
                                    @click="sliderImages.splice(idx, 1)"
                                    class="ppv2-absolute ppv2-top-2 ppv2-right-2 ppv2-bg-white ppv2-rounded-full ppv2-p-1 ppv2-shadow-md hover:ppv2-bg-red-50 ppv2-transition-colors"
                                >
                                    <X :size="14" class="ppv2-text-red-500" />
                                </button>
                            </div>
                            <BaseButton
                                v-if="sliderImages.length < 5"
                                variant="secondary"
                                :customFontSize="12"
                                @click="addSliderImage"
                            >
                                <Plus :size="12" /> {{ $t('website_settings.general.add_image') }}
                            </BaseButton>
                        </div>
                        <div v-if="errors.sliderImages" class="ppv2-mt-2 ppv2-text-red-500 ppv2-text-xs poppinsLight">
                            {{ errors.sliderImages[0] }}
                        </div>
                    </div>

                    <div v-else-if="selectedTemplate === 3">
                        <div v-for="(img, idx) in sliderImages" :key="img.id || idx" class="ppv2-relative ppv2-w-full ppv2-mb-6">
                            <CustomInputUpload
                                :label="idx === 0 ? $t('website_settings.general.image_right') : ''"
                                v-model="sliderImages[idx].url"
                                :required="idx === 0"
                                @input="(file) => handleSliderUpload(idx, file)"
                                :errorText="errors.sliderImages"
                                :enableCropper="false"
                            />
                            <button
                                v-if="sliderImages.length > 1"
                                type="button"
                                @click="sliderImages.splice(idx, 1)"
                                class="ppv2-absolute ppv2-top-2 ppv2-right-2 ppv2-bg-white ppv2-rounded-full ppv2-p-1 ppv2-shadow-md hover:ppv2-bg-red-50 ppv2-transition-colors ppv2-z-[60]"
                            >
                                <X :size="14" class="ppv2-text-red-500" />
                            </button>
                        </div>
                        <div v-if="sliderImages.length < 5" class="ppv2-flex ppv2-justify-center">
                            <BaseButton variant="secondary" :customFontSize="12" @click="addSliderImage">
                                <Plus :size="12" /> {{ $t('website_settings.general.add_image') }}
                            </BaseButton>
                        </div>
                    </div>

                    <div v-else-if="selectedTemplate === 4">
                        <CustomInputUpload
                            :label="$t('website_settings.general.background_video')"
                            v-model="videoData.url"
                            :required="true"
                            @input="(file) => handleVideoUpload(file)"
                            accept="video/*"
                            :maxSize="5 * 1024 * 1024"
                            :requiredTypeFileText="$t('experience.step2.requiredTypeFileVideo')"
                            :errorText="errors.video"
                        />
                    </div>
                </div>
            </div>
        </form>

        <!-- Delete Confirmation Dialog -->
        <CustomDialog v-model="showDeleteDialog" :defaultTitle="deleteDialogTitle" noScroll>
            <template #content>
                <div class="ppv2-text-center ppv2-py-4">
                    <div class="ppv2-mb-4">
                        <TriangleAlert :size="48" class="ppv2-text-yellow-500 ppv2-inline-block" />
                    </div>
                    <p class="ppv2-text-gray-700 dark:ppv2-text-gray-300 ppv2-text-base">
                        {{ deleteDialogMessage }}
                    </p>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    variant="outline"
                    @click="showDeleteDialog = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-sm !ppv2-min-w-[120px]`"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-sm !ppv2-min-w-[120px]`"
                >
                    {{ $t('common.confirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import SliderImageUpload from './SliderImageUpload.vue'
import BaseCheckbox from '../../../../Shared/BaseCheckbox.vue'
import BaseLabel from '../../../../Shared/BaseLabel.vue'
import CustomInput from '../../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import BaseSwitch from '../../../../Shared/BaseSwitch.vue'
import BaseColorInput from '../../../../Shared/BaseColorInput.vue'
import CustomInputUpload from '../../../../Inputs/CustomInputUpload.vue'
import CustomDialog from '../../../../Shared/CustomDialog.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import normalizeSize from '../../../../../utils/normalizeSize'
import { Check, Plus, X, TriangleAlert } from 'lucide-vue'
import normalizeFontSize from '../../../../../utils/normalizeFontSize'

export default {
    name: 'TabGeneral',
    components: {
        SliderImageUpload,
        BaseCheckbox,
        BaseLabel,
        CustomInput,
        BaseTextarea,
        BaseSwitch,
        BaseColorInput,
        CustomInputUpload,
        CustomDialog,
        BaseButton,
        Check,
        Plus,
        X,
        TriangleAlert,
    },
    props: {
        data: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            saving: false,
            selectedTemplate: null,
            sliderImages: [{ url: null, file: null, id: Math.random().toString(36).substr(2, 9) }],
            videoData: { url: null, file: null },
            showDeleteDialog: false,
            deleteDialogTitle: this.$t('common.confirm_delete'),
            deleteDialogMessage: '',
            deleteAction: null,
            deleteIndex: null,
            form: {
                logo_url: null,
                logo_file: null,
                delete_logo_file: false,
                title_welcome: '',
                desc_welcome: '',
                primary_color: '#e07850',
                secondary_color: '#f5f0e8',
                menus_color: '#1a1a1a',
                action_text_color: '#ffffff',
                enabled_section_res_desc: true,
                show_available_places: true,
                desc_reservation: '',
                image_rese_url: null,
                image_rese_file: null,
            },
            errors: {},
            // Réglages relus depuis la base après un save (source de vérité, sans reload de page)
            fetchedSettings: null,
        }
    },
    computed: {
        templates() {
            return this.data?.templates || []
        },
        settings() {
            return this.fetchedSettings || this.data?.settings || []
        },
    },
    mounted() {
        this.initializeForm()
    },
    activated() {
        this.$root.$off('website-settings-trigger-save-general', this.save)
        this.$root.$on('website-settings-trigger-save-general', this.save)
    },
    deactivated() {
        this.$root.$off('website-settings-trigger-save-general', this.save)
    },
    beforeDestroy() {
        this.$root.$off('website-settings-trigger-save-general', this.save)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        initializeForm() {
            const templates = this.templates || []
            const activeTemplate = templates.find((t) => t.is_active)
            if (activeTemplate) {
                this.selectedTemplate = activeTemplate.id
            } else if (templates.length > 0) {
                this.selectedTemplate = templates[0].id
            }

            const settings = this.settings || []
            settings.forEach((s) => {
                const v = s.value
                if (!v) return

                if (s.parameter === 'logo') {
                    this.form.logo_url = v
                } else if (s.parameter === 'primary_color') {
                    this.form.primary_color = v
                } else if (s.parameter === 'secondary_color') {
                    this.form.secondary_color = v
                } else if (s.parameter === 'menus_color') {
                    this.form.menus_color = v
                } else if (s.parameter === 'action_text_color') {
                    this.form.action_text_color = v
                } else if (s.parameter === 'desc_reservation') {
                    this.form.desc_reservation = v
                } else if (s.parameter === 'image_rese') {
                    this.form.image_rese_url = v
                } else if (s.parameter === 'enabled_section_res_desc') {
                    this.form.enabled_section_res_desc = v === 'on'
                } else if (s.parameter === 'show_available_places') {
                    this.form.show_available_places = v === 'on'
                }
            })

            this.loadTemplateTexts()
            this.loadSliderImages()
        },
        // Titre/sous-titre propres au template actif (comme l'image). Recalculés au changement de thème.
        loadTemplateTexts() {
            const settings = this.settings || []
            const pick = (param) => {
                const rows = settings.filter((s) => s.parameter === param && s.value)
                const active = rows.find((r) => Number(r.template_id) === Number(this.selectedTemplate))
                if (active) return active.value
                // repli uniquement sur une ancienne ligne globale (template_id vide), jamais un autre template
                const legacy = rows.find((r) => !r.template_id)
                return legacy ? legacy.value : ''
            }
            this.form.title_welcome = pick('title_welcome')
            this.form.desc_welcome = pick('desc_welcome')
        },
        // Relit les réglages depuis la base (après un save) → les valeurs par template sont à jour sans reload
        async refreshSettings() {
            try {
                const res = await axios.get('/back-office/website/website-settings/api')
                const fresh = res && res.data && res.data.currentSettings
                if (Array.isArray(fresh)) {
                    this.fetchedSettings = fresh
                    this.loadTemplateTexts()
                }
            } catch (e) {
                // silencieux : on garde les données courantes si la relecture échoue
            }
        },
        selectTemplate(id) {
            this.selectedTemplate = id
            this.loadTemplateTexts()
            this.loadSliderImages()
        },
        loadSliderImages() {
            const currentSettings = this.settings || []

            // Pour le template 4, charger depuis 'video' (ou fallback sur 'image_header' pour migration)
            if (this.selectedTemplate === 4) {
                const videoSetting = currentSettings.find(
                    (s) => s.parameter === 'video' && String(s.template_id) === String(this.selectedTemplate)
                )
                // Fallback: si pas de 'video', chercher dans 'image_header' (données anciennes)
                const fallbackSetting = currentSettings.find(
                    (s) => s.parameter === 'image_header' && String(s.template_id) === String(this.selectedTemplate)
                )
                const setting = videoSetting || fallbackSetting
                if (setting && setting.value) {
                    this.videoData = { url: setting.value.trim(), file: null }
                } else {
                    this.videoData = { url: null, file: null }
                }
                // Réinitialiser sliderImages pour éviter confusion
                this.sliderImages = [{ url: null, file: null, id: Math.random().toString(36).substr(2, 9) }]
                return
            }

            // Pour les autres templates, charger depuis 'image_header'
            const sliderSetting = currentSettings.find(
                (s) => s.parameter === 'image_header' && String(s.template_id) === String(this.selectedTemplate)
            )
            if (sliderSetting && sliderSetting.value) {
                const urls = sliderSetting.value.split(',')
                this.sliderImages = urls
                    .map((url) => ({
                        url: url.trim(),
                        file: null,
                        id: Math.random().toString(36).substr(2, 9),
                    }))
                    .filter((img) => img.url && img.url.trim() !== '')
            } else {
                this.sliderImages = []
            }
            if (this.sliderImages.length === 0) {
                this.sliderImages = [{ url: null, file: null, id: Math.random().toString(36).substr(2, 9) }]
            }
            // Réinitialiser videoData pour éviter confusion
            this.videoData = { url: null, file: null }
        },
        handleSliderUpload(idx, file) {
            console.log('idx', idx)
            console.log('file', file)
            if (file) {
                const reader = new FileReader()
                reader.onload = (ev) => {
                    this.sliderImages[idx].url = ev.target.result
                    this.sliderImages[idx].file = file
                }
                reader.readAsDataURL(file)
            } else {
                this.sliderImages[idx].url = null
                this.sliderImages[idx].file = null
            }
        },
        handleImageUpload(field, e) {
            console.log('field', field)
            const file = e.target.files[0]
            if (file) {
                const reader = new FileReader()
                reader.onload = (ev) => {
                    this.form[field + '_url'] = ev.target.result
                }
                reader.readAsDataURL(file)
                this.form[field + '_file'] = file
                if (field === 'logo') this.form.delete_logo_file = false
            } else {
                this.form[field + '_url'] = null
                this.form[field + '_file'] = null
                if (field === 'logo') this.form.delete_logo_file = true
            }
        },
        addSliderImage() {
            if (this.sliderImages.length < 5) {
                this.sliderImages.push({ url: null, file: null, id: Math.random().toString(36).substr(2, 9) })
            }
        },
        handleVideoUpload(file) {
            if (file) {
                const reader = new FileReader()
                reader.onload = (ev) => {
                    this.videoData.url = ev.target.result
                    this.videoData.file = file
                }
                reader.readAsDataURL(file)
            } else {
                this.videoData.url = null
                this.videoData.file = null
            }
        },
        scrollToField(refName) {
            this.$nextTick(() => {
                const c = this.$refs[refName]
                const el = c && (c.$el || c)
                if (!el || !el.scrollIntoView) return
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                // Surlignage temporaire pour montrer le champ à remplir
                const prevShadow = el.style.boxShadow
                const prevTransition = el.style.transition
                el.style.transition = 'box-shadow 0.3s ease'
                el.style.borderRadius = el.style.borderRadius || '12px'
                el.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.6)'
                setTimeout(() => {
                    el.style.boxShadow = prevShadow
                    el.style.transition = prevTransition
                }, 1800)
            })
        },
        async save() {
            this.errors = {}

            // Validation client — au 1er champ requis vide : message ciblé + scroll + surlignage du champ
            let mediaInvalid = false
            if (this.selectedTemplate === 4) {
                mediaInvalid = !this.videoData.url
            } else if (this.selectedTemplate === 1 || this.selectedTemplate === 3) {
                mediaInvalid = !this.sliderImages[0] || !this.sliderImages[0].url
            } else if (this.selectedTemplate === 2) {
                mediaInvalid = !this.sliderImages.some((img) => img.url)
            }

            const requiredChecks = [
                {
                    key: 'title_welcome',
                    ref: 'field_title_welcome',
                    invalid: !this.form.title_welcome,
                    msg: 'website_settings.general.errors.title_welcome_required',
                },
                {
                    key: 'desc_welcome',
                    ref: 'field_desc_welcome',
                    invalid: !this.form.desc_welcome,
                    msg: 'website_settings.general.errors.desc_welcome_required',
                },
                {
                    key: 'primary_color',
                    ref: 'field_primary_color',
                    invalid: this.form.primary_color === '#',
                    msg: 'website_settings.general.errors.primary_color_required',
                },
                {
                    key: 'secondary_color',
                    ref: 'field_secondary_color',
                    invalid: this.form.secondary_color === '#',
                    msg: 'website_settings.general.errors.secondary_color_required',
                },
                {
                    key: 'menus_color',
                    ref: 'field_menus_color',
                    invalid: this.form.menus_color === '#',
                    msg: 'website_settings.general.errors.menus_color_required',
                },
                {
                    key: 'action_text_color',
                    ref: 'field_action_text_color',
                    invalid: this.form.action_text_color === '#',
                    msg: 'website_settings.general.errors.action_text_color_required',
                },
                {
                    key: this.selectedTemplate === 4 ? 'video' : 'sliderImages',
                    ref: 'field_bannerMedia',
                    invalid: mediaInvalid,
                    msg: 'website_settings.general.errors.image_header_required',
                },
            ]

            const firstError = requiredChecks.find((c) => c.invalid)
            if (firstError) {
                this.errors = { [firstError.key]: [this.$t(firstError.msg)] }
                this.$toast.error(this.$t(firstError.msg))
                this.scrollToField(firstError.ref)
                return
            }

            this.saving = true
            this.$emit('save', { loading: true })
            try {
                const fd = new FormData()
                fd.append('template', this.selectedTemplate)
                const textFields = [
                    'primary_color',
                    'secondary_color',
                    'menus_color',
                    'action_text_color',
                    'title_welcome',
                    'desc_welcome',
                    'desc_reservation',
                ]
                textFields.forEach((f) => fd.append(f, this.form[f] || ''))
                fd.append('enabled_section_res_desc', this.form.enabled_section_res_desc ? 'on' : 'off')
                fd.append('show_available_places', this.form.show_available_places ? 'on' : 'off')

                if (this.form.logo_file) fd.append('logo', this.form.logo_file)
                if (this.form.delete_logo_file) fd.append('delete_logo_file', 1)
                if (this.form.image_rese_file) fd.append('image_rese', this.form.image_rese_file)

                // Pour template 4, envoyer sous la clé 'video'
                if (this.selectedTemplate === 4) {
                    if (this.videoData.file) {
                        fd.append('video', this.videoData.file)
                    } else if (this.videoData.url) {
                        fd.append('video_existing', this.videoData.url)
                    }
                } else {
                    // Pour les autres templates, envoyer sous 'image_header'
                    this.sliderImages.forEach((img) => {
                        img.file
                            ? fd.append('image_header[]', img.file)
                            : img.url
                              ? fd.append('image_header_existing[]', img.url)
                              : []
                    })
                }
                console.log(fd)

                const response = await axios.post('/back-office/website/website-settings', fd)
                this.$emit('save', { loading: false, success: true, message: response.data?.message })
                // Relire depuis la base : titre/sous-titre par template à jour au changement de thème, sans reload
                await this.refreshSettings()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('save', { loading: false })
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    this.$toast.error(this.$t('common.error_validate') || 'Erreur de validation')
                } else {
                    this.$toast.error(this.$t('website_settings.actions.save_error') || "Erreur lors de l'enregistrement")
                }
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.section-block {
    background: white;
    border-radius: 24px;
    border: 1px solid #f1f5f9;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.dark .section-block {
    background: #0f172a;
    border-color: #1e293b;
}

.template-card {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #f8fafc;
}

.dark .template-card {
    background: #1e293b;
    border-color: #334155;
}
</style>
